/**
 * Hook: useEventList
 * Manages event list state, filtering, pagination, and loading
 */

import { useState, useEffect, useCallback } from 'react';
import { getEventsPaginatedList } from '@/data/eventRepository';
import { supabase } from '@/supabase.config';
import type { EventListFilters, EventPagination, EventWithRelations } from '../types';
import { PAGINATION } from '@/constants';

export function useEventList() {
  const [events, setEvents] = useState<EventWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<EventListFilters>({
    search: '',
    status: 'all',
    startDateFrom: undefined,
    startDateTo: undefined,
  });
  const [pagination, setPagination] = useState<EventPagination>({
    page: 1,
    pageSize: PAGINATION.DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalItems: 0,
  });

  // Load events
  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getEventsPaginatedList(
        pagination.page,
        pagination.pageSize,
        {
          search: filters.search || undefined,
          status: filters.status === 'all' ? undefined : filters.status,
          startDateFrom: filters.startDateFrom || undefined,
          startDateTo: filters.startDateTo || undefined,
        },
        // Most recent / upcoming events first
        { field: 'start_date', direction: 'desc' },
      );

      // Participant counts + collected amount for the visible page (one query)
      const ids = result.data.map((e) => e.id);
      const counts = new Map<string, { participants: number; pending: number; revenue: number }>();
      if (ids.length > 0) {
        const { data: rows } = await supabase
          .from('event_participants')
          .select('event_id, registration_status, payment_amount')
          .in('event_id', ids);
        (rows ?? []).forEach((r) => {
          const c = counts.get(r.event_id) ?? { participants: 0, pending: 0, revenue: 0 };
          if (r.registration_status !== 'REJECTED') c.participants++;
          if (r.registration_status === 'PRE_REGISTERED') c.pending++;
          c.revenue += Number(r.payment_amount ?? 0);
          counts.set(r.event_id, c);
        });
      }

      setEvents(
        result.data.map((e) => ({
          ...e,
          participants_count: counts.get(e.id)?.participants ?? 0,
          pending_review_count: counts.get(e.id)?.pending ?? 0,
          revenue: counts.get(e.id)?.revenue ?? 0,
        })),
      );
      setPagination((prev) => ({
        ...prev,
        totalPages: result.totalPages,
        totalItems: result.count,
      }));
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  // Load events on mount and when dependencies change
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Update filters
  const updateFilters = useCallback((updates: Partial<EventListFilters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    // Reset to page 1 when filters change
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Change page
  const changePage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // Change page size
  const changePageSize = useCallback((pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize,
      page: 1, // Reset to first page
    }));
  }, []);

  // Reload events
  const reload = useCallback(() => {
    loadEvents();
  }, [loadEvents]);

  return {
    events,
    isLoading,
    filters,
    pagination,
    updateFilters,
    changePage,
    changePageSize,
    reload,
  };
}
